import { ChangeEvent, FC, FormEvent, useState } from 'react';
import { postRequest } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { User } from '../interfaces/user';

interface AuthFormProps {
  onSubmit: (formData: Record<string, string>) => void;
  title: string;
  fields: { name: string; label: string; type: string }[];
}

const AuthForm: FC<AuthFormProps> = ({ onSubmit, title, fields }) => {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    try {
      onSubmit(formData);
    } catch (error: any) {
      setErrorMessage(error.message || 'An error occurred.');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2>{title}</h2>

      {errorMessage && (
        <div className="alert alert-danger" role="alert">
          {errorMessage}
        </div>
      )}

      {fields.map((field) => (
        <div className="mb-3" key={field.name}>
          <label htmlFor={field.name} className="form-label">
            {field.label}
          </label>
          <input
            type={field.type}
            id={field.name}
            name={field.name}
            className="form-control"
            onChange={handleChange}
            required
          />
        </div>
      ))}

      <button type="submit" className="btn btn-primary">
        Submit
      </button>
    </form>
  );
};

const Login: React.FC<{
  onLogin: (token: string, user: User) => void;
}> = ({ onLogin }) => {
  const navigate = useNavigate();

  const handleLogin = async (data: Record<string, string>) => {
    try {
      const response = await postRequest('/auth/login', data, false);
      const token = response.token;
      const user: User = response.user;

      if (token && user) {
        onLogin(token, user); // Pass token and user to login function in useAuth hook
        navigate('/systems');
      } else {
        throw new Error('Login failed. Please try again.');
      }
    } catch (error: any) {
      alert(error.message || 'An error occurred during login.');
    }
  };

  return (
    <div className="container mt-5">
      <AuthForm
        onSubmit={handleLogin}
        title="Login"
        fields={[
          { name: 'username', label: 'Username', type: 'text' },
          { name: 'password', label: 'Password', type: 'password' },
        ]}
      />
    </div>
  );
};

export default Login;
